/**
 * Citation Verification Tool - Report Generator
 * 
 * Generates structured verification reports
 */

import { createFileLogger } from '../../../utils/logger';
import { CommandExecutionContext } from '../../../types/commands';
import {
  ExtractionResult,
  ResolutionResult,
  FetchResult,
  VerificationResult,
  VerificationReport,
  ReportSummary,
  Recommendation,
  AlignmentVerdict
} from './types';
import { truncateText } from './utils';

const log = createFileLogger('VERIFY_CITATIONS_REPORTER');

/**
 * Generate complete verification report
 */
export async function generateReport(
  manuscriptId: string,
  manuscriptName: string,
  extractions: ExtractionResult,
  resolutions: ResolutionResult[],
  fetchResults: FetchResult[],
  verifications: VerificationResult[],
  commentThreadIds: Map<string, string>,
  statistics: any,
  context: CommandExecutionContext
): Promise<{ reportId: string; markdownReportId: string }> {
  log.info('[generateReport] Generating report:', {
    manuscriptId,
    citationsCount: verifications.length
  });
  
  // Build report structure
  const report = buildReport(
    manuscriptId,
    manuscriptName,
    extractions,
    resolutions,
    fetchResults,
    verifications,
    statistics
  );
  
  // Create CitationVerificationReport entity with full structured data
  try {
    const useEntityStore = (await import('../../../stores/entityStore')).default;
    const { createEntity } = useEntityStore.getState().actions;
    
    const reportTitle = `Citation Verification - ${manuscriptName}`;
    
    log.info('[generateReport] Creating CitationVerificationReport entity:', {
      title: reportTitle,
      verificationsCount: verifications.length
    });
    
    // Build verification result data with all links and metadata
    const verificationData = verifications.map(v => {
      // Find the corresponding fetch result for this citation
      const fetchResult = fetchResults.find(f => 
        v.citation.linkedReferenceIds.includes(f.reference.id)
      );
      
      // Find the corresponding resolution for this citation
      const resolution = resolutions.find(r => 
        v.citation.linkedReferenceIds.includes(r.reference.id)
      );
      
      return {
        citationKey: v.citation.citationKey,
        claimText: v.citation.claimText,
        verdict: v.verdict,
        confidence: v.confidence,
        explanation: v.explanation,
        suggestedRevision: v.suggestedRevision,
        evidenceQuote: v.evidenceQuote,
        
        // Location in manuscript (for navigation)
        location: {
          charOffset: v.citation.location.charOffset,
          charLength: v.citation.location.charLength
        },
        
        // Comment thread ID (for linking to inline comment)
        commentThreadId: commentThreadIds.get(v.citation.citationKey),
        
        // Links - prefer fetchResult (has both existing and newly imported)
        localEntityId: fetchResult?.localEntityId,
        semanticScholarId: v.resolvedPaper?.semanticScholarId,
        doi: v.resolvedPaper?.doi,
        landingPageUrl: v.resolvedPaper?.landingPageUrl,
        
        // Paper metadata
        resolvedPaper: v.resolvedPaper ? {
          title: v.resolvedPaper.title,
          authors: v.resolvedPaper.authors,
          year: v.resolvedPaper.year,
          abstract: v.resolvedPaper.abstract,
          citationCount: v.resolvedPaper.citationCount
        } : undefined,
        
        // Resolution metadata
        resolutionConfidence: resolution?.confidence,
        matchScore: resolution?.confidence, // Use resolution confidence as match score for now
        fetchSource: fetchResult?.fetchSource || 
          (resolution?.localEntityId ? 'local_dataspace' as const : 'semantic_scholar' as const),
        
        // Issues
        issues: v.issues
      };
    });
    
    // Use entityActions.createEntity() for proper orchestration
    // This handles: backend call, local cache update, CRDT sync, event emission
    const newEntity = await createEntity({
      entityType: 'CitationVerificationReport',
      name: reportTitle,
      icon: '📋',
      spaceId: context.spaceId!,
      skeleton: {
        '@type': 'CitationVerificationReport',
        manuscriptId,
        manuscriptName,
        generatedAt: new Date().toISOString(),
        summary: report.summary,
        verifications: verificationData,
        statistics
      }
    });
    
    const reportId = newEntity._id || newEntity.id;
    
    log.info('[generateReport] Report entity created:', { 
      reportId, 
      title: reportTitle,
      citationsCount: verifications.length,
      issuesFound: report.summary.issueCountsByPriority.critical + report.summary.issueCountsByPriority.high
    });
    
    return { 
      reportId, 
      markdownReportId: reportId
    };
  } catch (error: any) {
    log.error('[generateReport] Failed to create report entity:', error);
    return { 
      reportId: 'report_creation_failed', 
      markdownReportId: 'report_creation_failed'
    };
  }
}

/**
 * Build report data structure
 */
function buildReport(
  manuscriptId: string,
  manuscriptName: string,
  extractions: ExtractionResult,
  resolutions: ResolutionResult[],
  fetchResults: FetchResult[],
  verifications: VerificationResult[],
  statistics: any
): VerificationReport {
  // Calculate summary statistics
  const summary = calculateSummary(extractions, verifications);
  
  // Extract resolution issues
  const referenceIssues = resolutions.flatMap(r => r.issues);
  
  // Generate recommendations
  const recommendations = generateRecommendations(verifications);
  
  return {
    reportId: `report_${Date.now()}`,
    manuscriptId,
    manuscriptName,
    generatedAt: new Date(),
    summary,
    referenceIssues,
    claimVerifications: verifications,
    recommendations,
    statistics
  };
}

/**
 * Calculate summary statistics
 */
function calculateSummary(
  extractions: ExtractionResult,
  verifications: VerificationResult[]
): ReportSummary {
  // Count verdicts
  const supportCounts: Record<AlignmentVerdict, number> = {
    strongly_supports: 0,
    supports: 0,
    weak_support: 0,
    not_addressed: 0,
    contradicts: 0,
    overstated_strength: 0,
    overstated_generalizability: 0,
    cherry_picked: 0,
    error: 0
  };
  
  for (const verification of verifications) {
    // Safeguard: treat undefined/invalid verdicts as errors
    const verdict = verification.verdict || 'error';
    if (verdict in supportCounts) {
      supportCounts[verdict]++;
    } else {
      console.warn('[calculateSummary] Unknown verdict:', verdict, 'for citation:', verification.citation.citationKey);
      supportCounts.error++;
    }
  }
  
  // Calculate issue priorities
  const issueCountsByPriority = {
    critical: verifications.filter(v => 
      v.verdict === 'contradicts' || 
      (v.overstatementDetails?.severity === 'critical')
    ).length,
    high: verifications.filter(v => 
      v.verdict === 'overstated_strength' || 
      v.verdict === 'overstated_generalizability' ||
      (v.overstatementDetails?.severity === 'major')
    ).length,
    medium: verifications.filter(v => 
      v.verdict === 'weak_support' ||
      v.verdict === 'cherry_picked'
    ).length,
    low: verifications.filter(v => 
      v.verdict === 'not_addressed'
    ).length
  };
  
  return {
    totalCitations: extractions.inTextCitations.length,
    totalReferences: extractions.references.length,
    verifiedCitations: verifications.length,
    unverifiedCitations: extractions.inTextCitations.length - verifications.length,
    supportCounts,
    issueCountsByPriority
  };
}

/**
 * Generate recommendations based on verifications
 */
function generateRecommendations(
  verifications: VerificationResult[]
): Recommendation[] {
  const recommendations: Recommendation[] = [];
  
  for (const verification of verifications) {
    if (verification.verdict === 'contradicts') {
      recommendations.push({
        priority: 'critical',
        type: 'replace_reference',
        citationId: verification.citation.id,
        message: `Citation ${verification.citation.citationKey} contradicts your claim`,
        suggestedAction: 'Find a different source or revise the claim'
      });
    } else if (verification.verdict === 'overstated_strength' || 
               verification.verdict === 'overstated_generalizability') {
      recommendations.push({
        priority: 'high',
        type: 'revise_claim',
        citationId: verification.citation.id,
        message: `Citation ${verification.citation.citationKey} overstates the evidence`,
        suggestedAction: verification.suggestedRevision || 'Tone down the language'
      });
    } else if (verification.verdict === 'weak_support') {
      recommendations.push({
        priority: 'medium',
        type: 'add_reference',
        citationId: verification.citation.id,
        message: `Citation ${verification.citation.citationKey} provides only weak support`,
        suggestedAction: 'Consider adding additional supporting references'
      });
    } else if (verification.verdict === 'not_addressed') {
      recommendations.push({
        priority: 'high',
        type: 'replace_reference',
        citationId: verification.citation.id,
        message: `Citation ${verification.citation.citationKey} does not address this claim`,
        suggestedAction: 'Verify you cited the correct paper'
      });
    }
  }
  
  return recommendations;
}

/**
 * Render report as markdown
 */
function renderReportMarkdown(report: VerificationReport): string {
  const { summary, claimVerifications, recommendations, referenceIssues } = report;
  
  let md = `# Citation Verification Report\n\n`;
  md += `**Manuscript:** ${report.manuscriptName}\n`;
  md += `**Generated:** ${report.generatedAt.toLocaleDateString()} ${report.generatedAt.toLocaleTimeString()}\n\n`;
  md += `---\n\n`;
  
  // Executive Summary
  md += `## Executive Summary\n\n`;
  md += `- **Total Citations:** ${summary.totalCitations}\n`;
  md += `- **Verified:** ${summary.verifiedCitations}\n`;
  md += `- **Issues Found:** ${summary.issueCountsByPriority.critical} critical, `;
  md += `${summary.issueCountsByPriority.high} high, ${summary.issueCountsByPriority.medium} medium\n\n`;
  
  // Support Distribution
  md += `### Support Distribution\n\n`;
  md += `- ✅ Strongly Supports: ${summary.supportCounts.strongly_supports}\n`;
  md += `- ✔️ Supports: ${summary.supportCounts.supports}\n`;
  md += `- ⚠️ Weak Support: ${summary.supportCounts.weak_support}\n`;
  md += `- ❓ Not Addressed: ${summary.supportCounts.not_addressed}\n`;
  md += `- ❌ Contradicts: ${summary.supportCounts.contradicts}\n`;
  md += `- 📢 Overstated Strength: ${summary.supportCounts.overstated_strength}\n`;
  md += `- 🌍 Overstated Generalizability: ${summary.supportCounts.overstated_generalizability}\n`;
  md += `- 🍒 Cherry Picked: ${summary.supportCounts.cherry_picked}\n\n`;
  
  md += `---\n\n`;
  
  // High Priority Issues
  const highPriorityIssues = claimVerifications.filter(v => 
    v.verdict === 'contradicts' || 
    v.verdict === 'overstated_strength' ||
    v.verdict === 'overstated_generalizability'
  ).sort((a, b) => b.confidence - a.confidence);
  
  if (highPriorityIssues.length > 0) {
    md += `## High Priority Issues\n\n`;
    
    for (const verification of highPriorityIssues) {
      const confidence = typeof verification.confidence === 'number' ? verification.confidence : 0;
      md += `### Citation ${verification.citation.citationKey}\n\n`;
      md += `**Claim:** "${truncateText(verification.citation.claimText, 200)}"\n\n`;
      md += `**Verdict:** ${getVerdictLabel(verification.verdict)} (${confidence}% confidence)\n\n`;
      md += `**Issue:** ${verification.explanation}\n\n`;
      
      if (verification.evidenceQuote) {
        md += `**Evidence from paper:** "${truncateText(verification.evidenceQuote, 300)}"\n\n`;
      }
      
      if (verification.suggestedRevision) {
        md += `**Suggested revision:** "${verification.suggestedRevision}"\n\n`;
      }
      
      md += `---\n\n`;
    }
  }
  
  // Reference Issues
  if (referenceIssues.length > 0) {
    md += `## Reference Issues\n\n`;
    
    for (const issue of referenceIssues) {
      md += `- **${issue.severity.toUpperCase()}:** ${issue.details}\n`;
    }
    
    md += `\n---\n\n`;
  }
  
  // Detailed Verification Results
  md += `## Detailed Verification Results\n\n`;
  md += `| Citation | Claim | Verdict | Confidence |\n`;
  md += `|----------|-------|---------|------------|\n`;
  
  for (const verification of claimVerifications) {
    const claimPreview = truncateText(verification.citation.claimText, 50);
    const verdictLabel = getVerdictLabel(verification.verdict);
    const confidence = typeof verification.confidence === 'number' ? verification.confidence : 0;
    md += `| ${verification.citation.citationKey} | ${claimPreview} | ${verdictLabel} | ${confidence}% |\n`;
  }
  
  md += `\n---\n\n`;
  
  // Recommendations
  if (recommendations.length > 0) {
    md += `## Recommendations\n\n`;
    
    const criticalRecs = recommendations.filter(r => r.priority === 'critical');
    const highRecs = recommendations.filter(r => r.priority === 'high');
    const mediumRecs = recommendations.filter(r => r.priority === 'medium');
    
    if (criticalRecs.length > 0) {
      md += `### Critical\n\n`;
      for (const rec of criticalRecs) {
        md += `1. ${rec.message}\n`;
        md += `   - Action: ${rec.suggestedAction}\n\n`;
      }
    }
    
    if (highRecs.length > 0) {
      md += `### High Priority\n\n`;
      for (const rec of highRecs) {
        md += `1. ${rec.message}\n`;
        md += `   - Action: ${rec.suggestedAction}\n\n`;
      }
    }
    
    if (mediumRecs.length > 0) {
      md += `### Medium Priority\n\n`;
      for (const rec of mediumRecs) {
        md += `1. ${rec.message}\n`;
        md += `   - Action: ${rec.suggestedAction}\n\n`;
      }
    }
  }
  
  return md;
}

/**
 * Get human-readable verdict label
 */
function getVerdictLabel(verdict: AlignmentVerdict): string {
  const labels: Record<AlignmentVerdict, string> = {
    strongly_supports: 'Strongly Supports',
    supports: 'Supports',
    weak_support: 'Weak Support',
    not_addressed: 'Not Addressed',
    contradicts: 'Contradicts',
    overstated_strength: 'Overstated Strength',
    overstated_generalizability: 'Overstated Generalizability',
    cherry_picked: 'Cherry Picked',
    error: 'Error'
  };
  
  // Safeguard against undefined verdicts
  if (!verdict) {
    return 'Unknown';
  }
  
  return labels[verdict] || String(verdict);
}

